import { createDeepEqualSelector } from "../utils";
import { selectUser } from "../users/selectors";
import { selectAllRecipients } from "../recipients/selectors";

import { formatDualApproval } from "./helpers";
import { isStoreNachaPassthroughDualApproval } from "./slice";

import type {
  DomesticWireDualApprovalState,
  DualApprovalSlice,
  PaginatedDualApproval,
  StatusType,
  UnknownDualApprovalState,
  PendingDomesticWireDualApprovalState,
  RespondedDomesticWireDualApprovalState,
  ACHDualApprovalState,
  ACHReversalDualApprovalState,
  BulkReversalDualApprovalState,
  FedNowDualApprovalState,
  PendingACHDualApprovalState,
  PendingACHReversalDualApprovalState,
  PendingBulkReversalDualApprovalState,
  RespondedACHDualApprovalState,
  RespondedACHReversalDualApprovalState,
  RespondedBulkReversalDualApprovalState,
  RespondedScheduledACHDualApprovalState,
  RespondedWireOrACHDualApprovalState,
  PendingWireOrACHDualApprovalState,
  ScheduledACHDualApprovalState,
  PendingScheduledACHDualApprovalState,
  PendingBulkDualApprovalState,
  BulkPaymentTemplateVersionDualApprovalState,
  PendingBulkPaymentTemplateVersionDualApprovalState,
  InternationalWireDualApprovalState,
  PendingInternationalWireDualApprovalState,
  PendingWireImportBatchDualApprovalState,
  RespondedWireImportBatchDualApprovalState,
  RespondedInternationalWireDualApprovalState,
  RespondedBulkPaymentTemplateVersionDualApprovalState,
  WireImportBatchDualApprovalState,
  PendingNachaPassthroughDualApprovalState,
  RespondedNachaPassthroughDualApprovalState,
} from "./types";
import type { RootState } from "../store";

const isDomesticWireFilter = (
  da: UnknownDualApprovalState,
): da is DomesticWireDualApprovalState => {
  return "wire" in da && !!da.wire;
};

const isACHFilter = (
  da: UnknownDualApprovalState,
): da is ACHDualApprovalState | ACHReversalDualApprovalState => {
  return "ach" in da && !!da.ach;
};

const isScheduledACHFilter = (
  da: UnknownDualApprovalState,
): da is ScheduledACHDualApprovalState => {
  return "scheduledACH" in da && !!da.scheduledACH;
};

const isFedNowFilter = (
  da: UnknownDualApprovalState,
): da is FedNowDualApprovalState => {
  return "fedNowTransaction" in da && !!da.fedNowTransaction;
};

const isBulkFilter = (
  da: UnknownDualApprovalState,
): da is PendingBulkDualApprovalState => {
  return "bulkPayment" in da && !!da.bulkPayment && !("bulkReversal" in da);
};

const isBulkReversalFilter = (
  da: UnknownDualApprovalState,
): da is BulkReversalDualApprovalState => {
  return "bulkReversal" in da && !!da.bulkReversal;
};

const isBulkTemplateFilter = (
  da: UnknownDualApprovalState,
): da is BulkPaymentTemplateVersionDualApprovalState => {
  return "bulkPaymentTemplateVersion" in da && !!da.bulkPaymentTemplateVersion;
};

const isInternationalWireFilter = (
  da: UnknownDualApprovalState,
): da is InternationalWireDualApprovalState => {
  return "international_wire" in da && !!da.international_wire;
};

const isWireImportBatchFilter = (
  da: UnknownDualApprovalState,
): da is WireImportBatchDualApprovalState => {
  return "wireImportBatch" in da && !!da.wireImportBatch;
};

export const sortDualApprovals = <T extends { created_at: string }>(
  a: T,
  b: T,
) => {
  if (a.created_at < b.created_at) {
    return 1;
  }
  if (a.created_at > b.created_at) {
    return -1;
  }

  return 0;
};

const selectDualApprovalState = (state: RootState) => state.dualApprovals;

const selectById = createDeepEqualSelector(
  selectDualApprovalState,
  (state) => state.byId,
);

const selectDualApprovalById = createDeepEqualSelector(
  [selectById, (_: RootState, id?: API.DualApprovalId | null) => id],
  (byId, id) => {
    if (!id) {
      return null;
    }

    return byId[id] ?? null;
  },
);

const selectPageMap = createDeepEqualSelector(
  selectDualApprovalState,
  (state) => state.pageMap ?? {},
);

export const selectTotal = (state: RootState) => state.dualApprovals.total ?? 0;

export const selectPaginatedDualApprovals = createDeepEqualSelector(
  [selectPageMap, selectById, (_, pageNumber: number) => pageNumber],
  (pageMap, byId, pageNumber) => {
    const ids = pageMap[pageNumber];
    return ids?.map((id) => byId[id]).filter(Boolean) ?? [];
  },
);

export const selectFormattedPaginatedDualApprovals = createDeepEqualSelector(
  [
    selectPageMap,
    selectById,
    selectAllRecipients,
    selectUser,
    (_, pageNumber: number) => pageNumber,
  ],
  (pageMap, byId, recipients, user, pageNumber) => {
    const ids = pageMap[pageNumber] ?? [];
    const currentUserId = user?.id;

    return ids
      .map((id): PaginatedDualApproval | null => {
        const dualApproval = byId[id];
        if (!dualApproval) return null;
        return formatDualApproval(dualApproval, recipients, currentUserId);
      })
      .filter((dualApproval): dualApproval is PaginatedDualApproval =>
        Boolean(dualApproval),
      );
  },
);

export const selectFormattedDualApproval = createDeepEqualSelector(
  [selectDualApprovalById, selectAllRecipients, selectUser],
  (dualApproval, recipients, user): PaginatedDualApproval | null => {
    if (!dualApproval) {
      return null;
    }

    return formatDualApproval(dualApproval, recipients, user?.id);
  },
);

const selectHistoryPageMap = createDeepEqualSelector(
  selectDualApprovalState,
  (state) => state.historyPageMap ?? {},
);

export const selectHistoryTotal = (state: RootState) =>
  state.dualApprovals.historyTotal ?? 0;

const selectTemplatePageMap = createDeepEqualSelector(
  selectDualApprovalState,
  (state) => state.templatePageMap ?? {},
);

export const selectTemplateTotal = (state: RootState) =>
  state.dualApprovals.templateTotal ?? 0;

export const selectFormattedPaginatedTemplateDualApprovals =
  createDeepEqualSelector(
    [
      selectTemplatePageMap,
      selectById,
      selectAllRecipients,
      selectUser,
      (_, pageNumber: number) => pageNumber,
    ],
    (templatePageMap, byId, recipients, user, pageNumber) => {
      const ids = templatePageMap[pageNumber] ?? [];
      const currentUserId = user?.id;

      return ids
        .map((id): PaginatedDualApproval | null => {
          const dualApproval = byId[id];
          if (!dualApproval) return null;
          return formatDualApproval(dualApproval, recipients, currentUserId);
        })
        .filter((dualApproval): dualApproval is PaginatedDualApproval =>
          Boolean(dualApproval),
        );
    },
  );

export const selectFormattedPaginatedHistoryDualApprovals =
  createDeepEqualSelector(
    [
      selectHistoryPageMap,
      selectById,
      selectAllRecipients,
      selectUser,
      (_, pageNumber: number) => pageNumber,
    ],
    (historyPageMap, byId, recipients, user, pageNumber) => {
      const ids = historyPageMap[pageNumber] ?? [];
      const currentUserId = user?.id;

      return ids
        .map((id): PaginatedDualApproval | null => {
          const dualApproval = byId[id];
          if (!dualApproval) return null;
          return formatDualApproval(dualApproval, recipients, currentUserId);
        })
        .filter((dualApproval): dualApproval is PaginatedDualApproval =>
          Boolean(dualApproval),
        );
    },
  );

export const selectDualApprovalForWire = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
  wireId: API.WireId,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId).filter(
    isDomesticWireFilter,
  );

  const dualApproval = dualApprovalValues.find((da) => {
    return da.wire === wireId;
  });

  return dualApproval;
};

export const selectDualApprovalForACH = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
  achId: API.ACHPaymentId,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId).filter(
    isACHFilter,
  );

  const dualApproval = dualApprovalValues.find((da) => {
    // Exclude ACH reversal dual approvals — they share the `ach` field
    // (pointing to the original payment being reversed) but represent a
    // different operation and should not be surfaced when looking up the
    // dual approval that gates the original ACH payment.
    if ("achReversal" in da) return false;
    return da.ach === achId;
  });

  return dualApproval;
};

export const selectDualApprovalForScheduledACH = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
  achId: API.ScheduledACHPaymentId,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId).filter(
    isScheduledACHFilter,
  );

  const dualApproval = dualApprovalValues.find((da) => {
    return da.scheduledACH === achId;
  });

  return dualApproval;
};

export const selectDualApprovalForFedNow = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
  fedNowTransactionId: API.FedNowTransactionId,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId).filter(
    isFedNowFilter,
  );

  const dualApproval = dualApprovalValues.find((da) => {
    return da.fedNowTransaction === fedNowTransactionId;
  });

  return dualApproval;
};

export const selectDualApprovalForBulk = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
  bulkId: API.BulkPaymentId | API.BulkReversalId,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId).filter(
    (da): da is PendingBulkDualApprovalState | BulkReversalDualApprovalState =>
      isBulkFilter(da) || isBulkReversalFilter(da),
  );

  const dualApproval = dualApprovalValues.find((da) => {
    // `bulkId` may be either the original bulk payment's id or (for reversals)
    // the bulk reversal's own id, depending on which drawer opened this lookup.
    // `BulkReversalDualApprovalState` carries both ids, so match on either.
    if ("bulkReversal" in da) {
      return da.bulkReversal === bulkId || da.bulkPayment === bulkId;
    }
    return da.bulkPayment === bulkId;
  });

  return dualApproval;
};

export const selectDualApprovalForInternationalWire = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
  internationalWireId: API.SwiftTransactionId,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId).filter(
    isInternationalWireFilter,
  );

  const dualApproval = dualApprovalValues.find((da) => {
    return da.international_wire === internationalWireId;
  });

  return dualApproval;
};

export const selectDualApproval = (
  state: RootState,
  id: API.DualApprovalId,
) => {
  return state.dualApprovals.byId[id];
};

export const selectWireDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);

  const filtered = dualApprovalValues
    .filter(isDomesticWireFilter)
    .sort(sortDualApprovals);

  return filtered;
};

export const selectACHDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);

  const filtered = dualApprovalValues
    .filter(isACHFilter)
    .sort(sortDualApprovals);

  return filtered;
};

export const selectScheduledACHDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);

  const filtered = dualApprovalValues
    .filter(isScheduledACHFilter)
    .sort(sortDualApprovals);

  return filtered;
};

export const selectBulkDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);
  const filtered = dualApprovalValues
    .filter(isBulkFilter)
    .sort(sortDualApprovals);
  return filtered;
};

export const selectBulkReversalDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);
  const filtered = dualApprovalValues
    .filter(isBulkReversalFilter)
    .sort(sortDualApprovals);
  return filtered;
};

export const selectBulkTemplateDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);
  const filtered = dualApprovalValues
    .filter(isBulkTemplateFilter)
    .sort(sortDualApprovals);
  return filtered;
};

export const selectInternationalWireDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);

  const filtered = dualApprovalValues
    .filter(isInternationalWireFilter)
    .sort(sortDualApprovals);

  return filtered;
};

export const selectWireImportBatchDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);

  const filtered = dualApprovalValues
    .filter(isWireImportBatchFilter)
    .sort(sortDualApprovals);

  return filtered;
};

export const selectNachaPassthroughDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) =>
  Object.values(state.dualApprovals.byId)
    .filter(isStoreNachaPassthroughDualApproval)
    .sort(sortDualApprovals);

export const selectAllDualApprovals = <
  S extends { dualApprovals: DualApprovalSlice },
>(
  state: S,
) => {
  const dualApprovalValues = Object.values(state.dualApprovals.byId);

  const sorted = dualApprovalValues.sort(sortDualApprovals);

  return sorted;
};

export const selectPendingDualApprovalsWires = createDeepEqualSelector(
  selectWireDualApprovals,
  (wireDualApprovals): PendingDomesticWireDualApprovalState[] => {
    const filtered = wireDualApprovals
      .filter((da): da is PendingDomesticWireDualApprovalState => {
        return da.responder === null;
      })
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectPendingDualApprovalsACH = createDeepEqualSelector(
  selectACHDualApprovals,
  (
    achDualApprovals,
  ): (PendingACHDualApprovalState | PendingACHReversalDualApprovalState)[] => {
    const filtered = achDualApprovals
      .filter(
        (
          da,
        ): da is
          | PendingACHDualApprovalState
          | PendingACHReversalDualApprovalState => {
          return da.responder === null;
        },
      )
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectPendingDualApprovalsScheduledACH = createDeepEqualSelector(
  selectScheduledACHDualApprovals,
  (achDualApprovals): PendingScheduledACHDualApprovalState[] => {
    const filtered = achDualApprovals
      .filter((da): da is PendingScheduledACHDualApprovalState => {
        return da.responder === null;
      })
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectPendingDualApprovalsBulk = createDeepEqualSelector(
  selectBulkDualApprovals,
  (bulkDualApprovals) => {
    const filtered = bulkDualApprovals
      .filter((da): da is PendingBulkDualApprovalState => {
        return da.responder === null;
      })
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectPendingDualApprovalsBulkReversal = createDeepEqualSelector(
  selectBulkReversalDualApprovals,
  (bulkReversalDualApprovals) => {
    const filtered = bulkReversalDualApprovals
      .filter((da): da is PendingBulkReversalDualApprovalState => {
        return da.responder === null;
      })
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectPendingDualApprovalsBulkTemplate = createDeepEqualSelector(
  selectBulkTemplateDualApprovals,
  (bulkTemplateDualApprovals) => {
    const filtered = bulkTemplateDualApprovals
      .filter(
        (da): da is PendingBulkPaymentTemplateVersionDualApprovalState => {
          return da.responder === null;
        },
      )
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectPendingDualApprovalsInternationalWires =
  createDeepEqualSelector(
    selectInternationalWireDualApprovals,
    (
      internationalWireDualApprovals,
    ): PendingInternationalWireDualApprovalState[] => {
      const filtered = internationalWireDualApprovals
        .filter((da): da is PendingInternationalWireDualApprovalState => {
          return da.responder === null;
        })
        .sort(sortDualApprovals);
      return filtered;
    },
  );

export const selectPendingDualApprovalsWireImportBatches =
  createDeepEqualSelector(
    selectWireImportBatchDualApprovals,
    (
      wireImportBatchDualApprovals,
    ): PendingWireImportBatchDualApprovalState[] => {
      const filtered = wireImportBatchDualApprovals
        .filter((da): da is PendingWireImportBatchDualApprovalState => {
          return da.responder === null;
        })
        .sort(sortDualApprovals);
      return filtered;
    },
  );

export const selectPendingDualApprovalsNachaPassthrough =
  createDeepEqualSelector(
    selectNachaPassthroughDualApprovals,
    (dualApprovals): PendingNachaPassthroughDualApprovalState[] =>
      dualApprovals.filter(
        (da): da is PendingNachaPassthroughDualApprovalState =>
          da.responder === null,
      ),
  );

export const selectAllPendingDualApprovals = createDeepEqualSelector(
  [
    selectPendingDualApprovalsWires,
    selectPendingDualApprovalsACH,
    selectPendingDualApprovalsScheduledACH,
    selectPendingDualApprovalsBulk,
    selectPendingDualApprovalsBulkReversal,
    selectPendingDualApprovalsBulkTemplate,
    selectPendingDualApprovalsInternationalWires,
    selectPendingDualApprovalsWireImportBatches,
    selectPendingDualApprovalsNachaPassthrough,
  ],
  (
    wireDAs,
    achDAs,
    scheduledACHDAs,
    bulkDAs,
    bulkReversalDAs,
    bulkTemplateDAs,
    internationalWireDAs,
    wireImportBatchDAs,
    nachaPassthroughDAs,
  ) => {
    const combined: PendingWireOrACHDualApprovalState[] = [
      ...wireDAs,
      ...achDAs,
      ...scheduledACHDAs,
      ...bulkDAs,
      ...bulkReversalDAs,
      ...bulkTemplateDAs,
      ...internationalWireDAs,
      ...wireImportBatchDAs,
      ...nachaPassthroughDAs,
    ];
    const filtered = combined.sort(sortDualApprovals);
    return filtered;
  },
);

export const selectRespondedDualApprovalsBulkTemplate = createDeepEqualSelector(
  selectBulkTemplateDualApprovals,
  (bulkTemplateDualApprovals) => {
    const filtered = bulkTemplateDualApprovals
      .filter(
        (da): da is RespondedBulkPaymentTemplateVersionDualApprovalState => {
          return da.responder !== null;
        },
      )
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectRespondedDualApprovalsWires = createDeepEqualSelector(
  selectWireDualApprovals,
  (wireDualApprovals) => {
    const filtered = wireDualApprovals
      .filter((da): da is RespondedDomesticWireDualApprovalState => {
        return da.responder !== null;
      })
      .sort(sortDualApprovals);

    return filtered;
  },
);

export const selectRespondedDualApprovalsACH = createDeepEqualSelector(
  selectACHDualApprovals,
  (achDualApprovals) => {
    const filtered = achDualApprovals
      .filter(
        (
          da,
        ): da is
          | RespondedACHDualApprovalState
          | RespondedACHReversalDualApprovalState => {
          return da.responder !== null;
        },
      )
      .sort(sortDualApprovals);

    return filtered;
  },
);

export const selectRespondedDualApprovalsBulkReversal = createDeepEqualSelector(
  selectBulkReversalDualApprovals,
  (bulkReversalDAs) => {
    const filtered = bulkReversalDAs
      .filter((da): da is RespondedBulkReversalDualApprovalState => {
        return da.responder !== null;
      })
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectRespondedDualApprovalsScheduledACH = createDeepEqualSelector(
  selectScheduledACHDualApprovals,
  (achDualApprovals) => {
    const filtered = achDualApprovals
      .filter((da): da is RespondedScheduledACHDualApprovalState => {
        return da.responder !== null;
      })
      .sort(sortDualApprovals);

    return filtered;
  },
);

export const selectRespondedDualApprovalsBulk = createDeepEqualSelector(
  selectBulkDualApprovals,
  (bulkDualApprovals) => {
    const filtered = bulkDualApprovals
      .filter(
        (
          da,
        ): da is PendingBulkDualApprovalState & {
          responder: { id: API.UserId; name: string };
        } => {
          return da.responder !== null;
        },
      )
      .sort(sortDualApprovals);
    return filtered;
  },
);

export const selectRespondedDualApprovalsInternationalWires =
  createDeepEqualSelector(
    selectInternationalWireDualApprovals,
    (internationalWireDualApprovals) => {
      const filtered = internationalWireDualApprovals
        .filter((da): da is RespondedInternationalWireDualApprovalState => {
          return da.responder !== null;
        })
        .sort(sortDualApprovals);

      return filtered;
    },
  );

export const selectRespondedDualApprovalsWireImportBatches =
  createDeepEqualSelector(
    selectWireImportBatchDualApprovals,
    (wireImportBatchDualApprovals) => {
      const filtered = wireImportBatchDualApprovals
        .filter((da): da is RespondedWireImportBatchDualApprovalState => {
          return da.responder !== null;
        })
        .sort(sortDualApprovals);

      return filtered;
    },
  );

export const selectRespondedDualApprovalsNachaPassthrough =
  createDeepEqualSelector(
    selectNachaPassthroughDualApprovals,
    (dualApprovals): RespondedNachaPassthroughDualApprovalState[] =>
      dualApprovals.filter(
        (da): da is RespondedNachaPassthroughDualApprovalState =>
          da.responder !== null,
      ),
  );

export const selectAllRespondedDualApprovalsWireOrACH = createDeepEqualSelector(
  [
    selectRespondedDualApprovalsWires,
    selectRespondedDualApprovalsACH,
    selectRespondedDualApprovalsScheduledACH,
    selectRespondedDualApprovalsBulk,
    selectRespondedDualApprovalsBulkReversal,
    selectRespondedDualApprovalsBulkTemplate,
    selectRespondedDualApprovalsInternationalWires,
    selectRespondedDualApprovalsWireImportBatches,
    selectRespondedDualApprovalsNachaPassthrough,
  ],
  (
    wireDAs,
    achDAs,
    scheduledACHDAs,
    bulkDAs,
    bulkReversalDAs,
    bulkTemplateDAs,
    internationalWireDAs,
    wireImportBatchDAs,
    nachaPassthroughDAs,
  ) => {
    const combined: RespondedWireOrACHDualApprovalState[] = [
      ...wireDAs,
      ...achDAs,
      ...scheduledACHDAs,
      ...bulkDAs,
      ...bulkReversalDAs,
      ...bulkTemplateDAs,
      ...internationalWireDAs,
      ...wireImportBatchDAs,
      ...nachaPassthroughDAs,
    ];
    const filtered = combined.sort(sortDualApprovals);
    return filtered;
  },
);

export const selectWireDualApprovalsByStatus = createDeepEqualSelector(
  [selectWireDualApprovals, (_state, status: StatusType) => status],
  (wireDualApprovals, status) => {
    return wireDualApprovals.filter((da) => da.status === status);
  },
);

export const selectACHDualApprovalsByStatus = createDeepEqualSelector(
  [selectACHDualApprovals, (_state, status: StatusType) => status],
  (achDualApprovals, status) => {
    return achDualApprovals.filter((da) => da.status === status);
  },
);

export const selectInternationalWireDualApprovalsByStatus =
  createDeepEqualSelector(
    [
      selectInternationalWireDualApprovals,
      (_state, status: StatusType) => status,
    ],
    (internationalWireDualApprovals, status) => {
      return internationalWireDualApprovals.filter(
        (da) => da.status === status,
      );
    },
  );

export const selectIsDualApprovalRequired = createDeepEqualSelector(
  selectUser,
  (user) => {
    if (user === null || user === undefined) return false;
    return user.dual_approval_required || false;
  },
);
